<script setup lang="ts">
import { ref, computed, watch, onMounted, type Ref } from 'vue'
import type MenuItem from '../../../type/MenuItem'
import TitlebarMenuView from './TitlebarMenuView.vue'
import { titleBar } from '@/composables/menu/title'
import { debounce } from 'perfect-debounce'
import { useRouter } from 'vue-router'

const router = useRouter()

const handleBack = () => {
  titleBar.value.back(router)
}

const props = defineProps({
  menuCollapse: {
    type: Boolean,
    required: false,
    default: false
  }
})

const MAX_DEPANDENT_COUNT = 4

const isCollapse = ref(props.menuCollapse)

watch(
  () => props.menuCollapse,
  (val) => {
    isCollapse.value = val
  }
)

const emits = defineEmits(['collapse'])

function onCollapseClicked() {
  isCollapse.value = !isCollapse.value
  emits('collapse', isCollapse.value)
}

function onMenuClicked(item: MenuItem, index: number) {
  console.log('onMenuClicked', item, menuRef.value?.children[index])
  if (titleBar.value.onMenuClicked !== null) {
    titleBar.value.onMenuClicked(item, menuRef.value != null ? menuRef.value.children[index] : null)
  }
}

function onContextMenuClicked(item: MenuItem, index: number) {
  if (titleBar.value.onMenuClicked !== null) {
    titleBar.value.onMenuClicked(item, moreRef.value != null ? moreRef.value : null)
  }
}

let moreRef: Ref<HTMLElement | null> = ref(null)
let menuRef: Ref<HTMLElement | null> = ref(null)

let innerWidth = ref(1024)

onMounted(() => {
  innerWidth.value = window.innerWidth
  window.addEventListener('resize', (e) => debounced())
})

const debounced = debounce(() => {
  if (typeof window !== 'undefined') {
    innerWidth.value = window.innerWidth
  }
}, 100)

const isMd = computed(() => {
  return innerWidth.value >= 768
})

let showSize = computed(() => {
  const normalMinSize = innerWidth.value <= 768 ? 2 : innerWidth.value <= 1280 ? 3 : 4
  return Math.min(normalMinSize, titleBar.value.showMenuCount)
})

let menusShow = computed(() => {
  return titleBar.value.menus != undefined ? titleBar.value.menus?.slice(0, showSize.value) : []
})

let menusContext = computed(() => {
  return titleBar.value.menus != undefined && titleBar.value.menus.length > showSize.value
    ? titleBar.value.menus?.slice(showSize.value, titleBar.value.menus.length)
    : []
})
</script>
<template>
  <header
    v-show="titleBar.backable || isMd"
    relative
    bg="[rgba(var(--c-sidebar),0.7)]"
    :class="titleBar.transparent ? 'bg-transparent' : 'bg-transparent md:backdrop-blur-md'"
    z-1
    h-14>
    <div absolute top-0 z-1 left-3 h-full w-auto flex-r-center>
      <ClientOnly>
        <RIcon
          v-if="isMd"
          :icon="isCollapse ? 'i-tdesign:menu-unfold' : 'i-grommet-icons:menu'"
          :reverse="titleBar.reverse"
          size="medium"
          @click="onCollapseClicked"
          hover:bg-primary-light />
      </ClientOnly>
      <div
        v-if="titleBar.backable && !isMd"
        h-8
        w-8
        rounded-full
        flex="~ col"
        items-center
        justify-center
        opacity-60
        cursor-pointer
        backdrop-blur-md
        bg-gray-200
        hover:bg-gray-300
        active:bg-gray-300
        text-main
        hover:text-second
        active:text-second
        :size="'small'"
        @click="handleBack">
        <div class="i-material-symbols-light:arrow-back-ios" text-lg ml-1.4></div>
      </div>
      <div
        v-if="titleBar.backable && !isMd"
        h-8
        w-8
        rounded-full
        flex="~ col"
        items-center
        justify-center
        opacity-60
        cursor-pointer
        backdrop-blur-md
        bg-gray-200
        hover:bg-gray-300
        active:bg-gray-300
        text-main
        ml-2
        hover:text-second
        active:text-second
        :size="'small'"
        @click="router.replace('/home')">
        <div i-fluent:home-empty-20-regular text-xl mb-1px></div>
      </div>
    </div>
    <div v-if="isMd" absolute left-0 right-0 top-0 h-full w-full flex-r-center grow>
      <div
        v-if="titleBar.backable && !titleBar.hideBack"
        h-9
        w-9
        p-2.5
        rounded-full
        flex="~ col"
        items-center
        btn-hint
        :size="'small'"
        @click="handleBack">
        <div class="i-ic:twotone-arrow-back-ios" w-4 h-4 text-main></div>
      </div>
      <h1 :class="{ 'ml-1': true, 'text-inverted': titleBar.titleReverse }" text-center text-base text-lg font-bold>
        {{ titleBar.title }}
      </h1>
    </div>
    <div absolute right-0 top-0 z1 h-full min-w-10 w-auto flex-r-center>
      <div ref="menuRef" v-show="(titleBar.menus?.length || 0) > 0" flex="~ row" mr-4 items-center>
        <template v-for="(item, index) in menusShow" :key="index">
          <el-tooltip
            v-if="item.type !== 'button' && isMd"
            :content="item.name"
            placement="bottom"
            effect="light"
            :show-after="500">
            <RIcon
              :icon="item.icon"
              :badge="item.extras?.badge || 0"
              :reverse="titleBar.reverse"
              size="medium"
              mr-1
              hover:bg-primary-light
              cursor-pointer
              @click="onMenuClicked(item, index)">
            </RIcon>
          </el-tooltip>
          <div
            v-if="!isMd"
            flex="~ row"
            items-center
            btn-hint
            py-1.5
            :class="titleBar.reverse ? 'text-inverted' : 'text-main'"
            cursor-pointer
            @click="onMenuClicked(item, index)"
            mr-0.5>
            <div :class="item.icon"></div>
            <div ml-1 text-sm>{{ item.name }}</div>
          </div>
          <button
            v-if="item.type === 'button' && isMd"
            mr-1
            flex-r-center
            rounded
            px-3
            py-1.5
            btn-trans
            @click="onMenuClicked(item, index)"
            :class="titleBar.reverse ? 'text-inverted' : 'text-base'">
            <i :class="item.icon" mr-1 inline-block text-xl></i
            ><span text-sm v-if="innerWidth >= 768">{{ item.name }}</span>
          </button>
        </template>
        <TitlebarMenuView
          v-if="menusContext.length > 0 && isMd"
          :menus="menusContext"
          @onItemClicked="onContextMenuClicked">
          <template #trigger>
            <RIcon ref="moreRef" icon="i-iconamoon:menu-kebab-vertical-bold" :reverse="titleBar.reverse" size="medium">
            </RIcon>
          </template>
        </TitlebarMenuView>
      </div>
    </div>
  </header>
</template>
<style lang="" scoped></style>
